import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDashboardUi } from '../../config/dashboardUiTheme';
import { cn } from '../../lib/utils';
import { useComplaints } from '../../hooks/useEntities';

const ComplaintOverview = ({ refreshKey }) => {
  const { theme } = useTheme();
  const dash = useDashboardUi(theme);
  const { data: complaints = [] } = useComplaints();

  const complaintData = useMemo(() => {
    const statusCounts = {
      'Open': 0,
      'In Progress': 0,
      'Under Investigation': 0,
      'Resolved': 0,
      'Closed': 0,
      'Overdue': 0
    };

    const priorityCounts = {
      'Critical': 0,
      'High': 0,
      'Medium': 0,
      'Low': 0
    };

    const categoryCounts = {};
    const departmentCounts = {};
    const sourceCounts = {};

    complaints.forEach(complaint => {
      // Status counts
      const status = complaint.status?.name || 'Open';
      statusCounts[status] = (statusCounts[status] || 0) + 1;

      // Check if overdue
      if (complaint.dueDate && new Date(complaint.dueDate) < new Date() && 
          !['Resolved', 'Closed'].includes(status)) {
        statusCounts['Overdue'] = (statusCounts['Overdue'] || 0) + 1;
      }

      // Priority counts
      const priority = complaint.priority?.name || 'Medium';
      priorityCounts[priority] = (priorityCounts[priority] || 0) + 1;

      // Category counts
      const category = complaint.category?.name || 'General';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;

      // Department counts
      const department = complaint.department?.name || 'Unknown';
      departmentCounts[department] = (departmentCounts[department] || 0) + 1;

      // Source counts
      const source = complaint.source?.name || 'Customer';
      sourceCounts[source] = (sourceCounts[source] || 0) + 1;
    });

    return {
      statusCounts,
      priorityCounts,
      categoryCounts,
      departmentCounts,
      sourceCounts,
      total: complaints.length,
      openCount: statusCounts.Open + statusCounts['In Progress'] + statusCounts['Under Investigation'],
      resolvedCount: statusCounts.Resolved + statusCounts.Closed
    };
  }, [complaints]);

  const getStatusSemantic = (status) => {
    switch (status) {
      case 'Open':
        return 'warning';
      case 'In Progress':
      case 'Under Investigation':
        return 'info';
      case 'Resolved':
      case 'Closed':
        return 'success';
      case 'Overdue':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const getPrioritySemantic = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'danger';
      case 'High':
        return 'warning';
      case 'Medium':
        return 'info';
      case 'Low':
        return 'success';
      default:
        return 'neutral';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'In Progress':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'Under Investigation':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'Resolved':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'Closed':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'Overdue':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  return (
    <div className={`${dash.widgetShell()}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className={`text-xl font-semibold ${theme.text.primary}`}>
          Complaint Overview
        </h2>
        <div className={`px-3 py-1 ${dash.pill('warning')}`}>
          {complaintData.total} Complaints
        </div>
      </div>

      <div className="space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-3 gap-3">
          <div className={`${dash.statCard()} text-center`}>
            <div className={`text-lg font-bold ${theme.text.primary} mb-1`}>
              {complaintData.openCount}
            </div>
            <div className={`text-xs ${theme.text.secondary}`}>
              Open
            </div>
          </div>
          <div className={`${dash.statCard()} text-center`}>
            <div className={`text-lg font-bold ${theme.text.primary} mb-1`}>
              {complaintData.resolvedCount}
            </div>
            <div className={`text-xs ${theme.text.secondary}`}>
              Resolved
            </div>
          </div>
          <div className={`${dash.statCard()} text-center`}>
            <div className={`text-lg font-bold ${theme.text.primary} mb-1`}>
              {complaintData.statusCounts.Overdue}
            </div>
            <div className={`text-xs ${theme.text.secondary}`}>
              Overdue
            </div>
          </div>
        </div>

        {/* Status Distribution */}
        <div>
          <h3 className={`text-sm font-medium ${theme.text.secondary} mb-3`}>Status Distribution</h3>
          <div className="space-y-2">
            {Object.entries(complaintData.statusCounts)
              .filter(([status]) => status !== 'Overdue')
              .map(([status, count]) => (
                <div key={status} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={cn('shrink-0', dash.iconText(getStatusSemantic(status)))}>
                      {getStatusIcon(status)}
                    </span>
                    <span className={cn('text-sm truncate', theme.text.primary)}>{status}</span>
                  </div>
                  <span className={cn('text-sm font-medium tabular-nums shrink-0', theme.text.primary)}>
                    {count}
                  </span>
                </div>
              ))}
          </div>
        </div>

        {/* Priority Distribution */}
        <div>
          <h3 className={`text-sm font-medium ${theme.text.secondary} mb-3`}>Priority Distribution</h3>
          <div className="space-y-2">
            {Object.entries(complaintData.priorityCounts).map(([priority, count]) => (
              <div key={priority} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={cn('w-2.5 h-2.5 rounded-full shrink-0', dash.dot(getPrioritySemantic(priority)))}
                    aria-hidden
                  />
                  <span className={cn('text-sm truncate', theme.text.primary)}>{priority}</span>
                </div>
                <span className={cn('text-sm font-medium tabular-nums shrink-0', theme.text.primary)}>
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Categories */}
        <div>
          <h3 className={`text-sm font-medium ${theme.text.secondary} mb-3`}>Top Categories</h3>
          <div className="space-y-2">
            {Object.entries(complaintData.categoryCounts)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 3)
              .map(([category, count]) => (
                <div key={category} className="flex items-center justify-between">
                  <span className={`text-sm ${theme.text.primary} truncate`}>
                    {category}
                  </span>
                  <span className={`text-sm font-medium ${theme.text.primary}`}>
                    {count}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplaintOverview;
